import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, MenuItem, TextField, Typography } from '@mui/material';
import { v4 as uuid } from 'uuid';
import { supabase } from '../../supabaseClient';
import InvoiceReceiptDialog from './InvoiceReceiptDialog';

// Scope this controller to one appointment/type. Only the document RPC writes;
// payment, refund, credit and appointment APIs are deliberately not involved.
export default function BillingDocumentDialog({ sourceLoading = false, sourceError = false, onRetrySource, ...props }) {
  const { selected, docType, open, onClose } = props;
  const [history, setHistory] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const pending = useRef(null);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    if (!open || !selected?.id) return undefined;
    let active = true;
    setHistory(null);
    setLoadError(false);
    supabase.rpc('scribe_billing_document_history', { p_appointment_id: selected.id, p_kind: docType })
      .then(({ data, error }) => {
        if (error || !Array.isArray(data)) throw new Error('Document history unavailable');
        if (active) { setHistory(data); setViewId(data[0]?.id || null); }
      }).catch(() => { if (active) setLoadError(true); });
    return () => { active = false; };
  }, [open, selected?.id, docType, attempt]);
  const documentRecord = history?.find(item => item.id === viewId) || null;
  const save = useCallback(async ({ snapshot, originalSnapshot = null, reason = null }) => {
    const payload = {
      p_appointment_id: selected.id, p_kind: docType, p_snapshot: snapshot,
      p_expected_id: documentRecord?.id || null,
      p_original_snapshot: documentRecord ? null : originalSnapshot,
      p_reason: reason,
    };
    const fingerprint = JSON.stringify(payload);
    if (pending.current?.fingerprint !== fingerprint) pending.current = { fingerprint, id: uuid() };
    const { data, error } = await supabase.rpc('scribe_save_billing_document', { ...payload, p_request_id: pending.current.id });
    if (error || !Array.isArray(data) || !data.length) throw new Error('Document not saved');
    if (mounted.current) { setHistory(data); setViewId(data[0].id); pending.current = null; }
  }, [selected.id, docType, documentRecord]);
  if (!open) return null;
  if (!history || (!history.length && sourceLoading) || (!history.length && sourceError)) {
    return <Dialog aria-labelledby="billing-document-loading-title" open onClose={onClose} fullWidth maxWidth="md"><DialogContent>
      <Typography id="billing-document-loading-title" variant="h6">Billing document</Typography>
      {loadError || sourceError ? <Alert severity="error" action={<Button onClick={() => { setAttempt(value => value + 1); onRetrySource?.(); }}>Retry</Button>}>
        {loadError ? 'Could not load document history. Please retry.' : 'Could not load the original document details. Please retry.'}
      </Alert> : <Box role="status"><CircularProgress size={24} /> Loading document…</Box>}
      <Button onClick={onClose}>Close document</Button>
    </DialogContent></Dialog>;
  }
  return <InvoiceReceiptDialog {...props} key={documentRecord?.id || 'unrecorded'} documentRecord={documentRecord}
    onIssue={documentRecord ? undefined : snapshot => save({ snapshot })} onReplace={save}
    historyControls={saving => <Box sx={{ my: 2 }}>
      {history.length > 0 && <>
        <TextField select fullWidth disabled={saving} label="Document history" value={viewId || ''} onChange={event => setViewId(event.target.value)}>
          {history.map(item => <MenuItem key={item.id} value={item.id}>{item.number} — {item.status === 'void' ? 'VOID' : 'Issued'}</MenuItem>)}
        </TextField>
        <Typography variant="caption">Issued by {documentRecord?.created_by} on {props.formatDate(documentRecord?.created_at)}</Typography>
        {documentRecord?.replaces_id && <Button disabled={saving} onClick={() => setViewId(documentRecord.replaces_id)}>View original</Button>}
        {documentRecord?.replacement_id && <Button disabled={saving} onClick={() => setViewId(documentRecord.replacement_id)}>View replacement</Button>}
      </>}
    </Box>} />;
}
